'use client';

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  mobileGraphql,
  mobileKeys,
  type MobileConnection,
  type MobileGraphqlEndpoint,
} from '@/shared/mobile-service/api/cors-mobile-api';

export type ArtifactRow = Record<string, unknown>;

interface UseArtifactListArgs {
  mobileAcquisitionId: number;
  graphqlType: string;
  endpoint: MobileGraphqlEndpoint;
  rootQuery: string;
  /** Fully-built GraphQL query string (args inlined), or null when unbuildable. */
  query: string | null;
  /** Stable serialization of paging/sort/filter state for the query key. */
  keyParams: Record<string, unknown>;
}

/**
 * Fetch one page of an artifact list. The query is prebuilt by the caller
 * ({@link buildArtifactListQuery}); this hook wires it to the cell session and
 * TanStack Query, unwrapping the ``<rootQuery>`` field from the response.
 */
export function useArtifactList(
  args: UseArtifactListArgs,
): UseQueryResult<MobileConnection<ArtifactRow>, Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();
  const { mobileAcquisitionId, graphqlType, endpoint, rootQuery, query, keyParams } = args;

  return useQuery<MobileConnection<ArtifactRow>, Error>({
    queryKey: mobileKeys.artifactList(cellId, mobileAcquisitionId, graphqlType, keyParams),
    enabled: enabled && Boolean(query),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const data = await mobileGraphql<Record<string, MobileConnection<ArtifactRow>>>({
        cellId: cellId as string,
        accessToken: accessToken as string,
        endpoint,
        query: query as string,
        signal,
      });
      const connection = data[rootQuery];
      if (!connection) {
        throw new Error(`Missing "${rootQuery}" in mobile-service response`);
      }
      return connection;
    },
  });
}
